import {
	createActionsHook,
	createContainer,
	createSelector,
	createStateHook,
	createStore,
	type StoreActionApi,
} from 'react-sweet-state';
import type {
	Transition,
	Workflow,
	Column,
	IssueType,
} from '@atlassian/jira-business-board-workflow-issues';

type WorkflowStoreState = {
	columns: Column[];
	issueTypes: IssueType[];
	transitions: Transition[];
	workflowId: string | null;
	workflows: Workflow[];
	workflowIssuesLastChangedTime?: string;
	workflowName: string | null;
};

const initialState: WorkflowStoreState = {
	columns: [],
	issueTypes: [],
	transitions: [],
	workflows: [],
	workflowId: null,
	workflowName: null,
};

const removeColumn =
	(statusId: number) =>
	({ setState, getState }: StoreActionApi<WorkflowStoreState>) => {
		const { columns, workflows } = getState();
		const newColumns = columns.filter((c) => c.statusId !== statusId);

		setState({
			columns: [...newColumns],
			workflows: workflows.map((wf) => {
				const newWfColumns = wf.columns.filter((c) => c.statusId !== statusId);
				return {
					...wf,
					columns: [...newWfColumns],
				};
			}),
		});
	};

const setColumns =
	(columns: Column[]) =>
	({ setState, getState }: StoreActionApi<WorkflowStoreState>) => {
		const { workflowId, workflows } = getState();
		setState({
			columns,
			workflows: workflows.map((workflow) =>
				workflow.workflowId === workflowId ? { ...workflow, columns } : workflow,
			),
		});
	};

const setFetchResponse =
	({
		workflows,
		activeWorkflow,
		workflowIssuesLastChangedTime,
	}: {
		workflows: Workflow[];
		activeWorkflow: Workflow;
		workflowIssuesLastChangedTime?: string;
	}) =>
	async ({ setState }: StoreActionApi<WorkflowStoreState>) => {
		setState({
			columns: activeWorkflow.columns,
			issueTypes: activeWorkflow.issueTypes,
			transitions: activeWorkflow.transitions,
			workflows,
			workflowId: activeWorkflow.workflowId,
			workflowIssuesLastChangedTime,
			workflowName: activeWorkflow.workflowName,
		});
	};

const actions = {
	removeColumn,
	setColumns,
	setFetchResponse,
};

export const WorkflowStoreContainer = createContainer();

const WorkflowStore = createStore({
	initialState,
	actions,
	name: 'WorkflowStore',
	containedBy: WorkflowStoreContainer,
});

const getInitialTransition = createSelector(
	(state: WorkflowStoreState) => state.transitions,
	(transitions) => transitions.find((transition) => transition.isInitial),
);

export const useWorkflowStoreState = createStateHook(WorkflowStore);

export const useWorkflowStoreActions = createActionsHook(WorkflowStore);

export const useInitialTransition = createStateHook<
	WorkflowStoreState,
	typeof actions,
	ReturnType<typeof getInitialTransition>
>(WorkflowStore, {
	selector: getInitialTransition,
});
